import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BookingModal from '../../components/feature/BookingModal';
import VehicleList from './components/VehicleList';
import { SEO } from '../../utils/seo';

export default function CompactCarPage() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "name": "소형차 렌트 - 대전렌트카",
        "description": "경제적이고 연비가 좋은 소형차 렌트 서비스. 대전 지역 최저가 보장, 다양한 소형차 차량 보유. 현대 아반떼, 기아 K3 등 인기 소형차 렌트.",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/compact-car`,
        "isPartOf": {
          "@type": "WebSite",
          "name": "대전렌트카",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
        }
      },
      {
        "@type": "ItemList",
        "name": "대전 소형차 렌트 차량 목록",
        "description": "대전렌트카 소형차 렌트 차량 목록",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/compact-car`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "현대 캐스퍼 인스퍼레이션",
            "description": "경제적인 소형 SUV, 1일 렌트 ₩60,000"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "기아 레이 시그니처",
            "description": "넓은 실내 공간의 소형차, 1일 렌트 ₩70,000"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "기아 모닝 프레스트지",
            "description": "연비 좋은 경차, 1일 렌트 ₩70,000"
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO
        title="소형차 렌트 대전 최저가 | 대전렌트카"
        description="경제적이고 연비가 좋은 소형차 렌트 서비스. 대전 지역 최저가 보장, 다양한 소형차 차량 보유. 현대 아반떼, 기아 K3 등 인기 소형차 렌트."
        keywords="대전 소형차 렌트, 소형차 렌터카, 아반떼 렌트, K3 렌트, 경제적인 렌터카"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/compact-car`}
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-20 md:pt-24 pb-12 md:pb-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
                소형차 렌트
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
                경제적이고 연비가 좋은 소형차로 도심 주행에 최적화되어 있습니다.<br className="hidden sm:block" />
                합리적인 가격으로 편리한 이동을 경험하세요.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 md:mb-12 px-4">
              <a 
                href="tel:010-5437-1535"
                className="w-full bg-gray-900 text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap block text-center"
              >
                지금 예약하기
              </a>
            </div>
          </div>
        </section>

        {/* Vehicle List */}
        <VehicleList />

        {/* Features Section */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-8 md:mb-12">
              소형차의 장점
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              <div className="bg-white rounded-xl p-5 md:p-6 shadow-md text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-primary to-secondary rounded-full mx-auto mb-3 md:mb-4">
                  <i className="ri-gas-station-line text-white text-2xl md:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">우수한 연비</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600">경제적인 연료 소비로 비용 절감</p>
              </div>

              <div className="bg-white rounded-xl p-5 md:p-6 shadow-md text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-primary to-secondary rounded-full mx-auto mb-3 md:mb-4">
                  <i className="ri-steering-2-line text-white text-2xl md:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">편리한 주차</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600">작은 크기로 주차 공간 확보 용이</p>
              </div>

              <div className="bg-white rounded-xl p-5 md:p-6 shadow-md text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-primary to-secondary rounded-full mx-auto mb-3 md:mb-4">
                  <i className="ri-wallet-line text-white text-2xl md:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">합리적 가격</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600">저렴한 렌트 비용으로 부담 없이</p>
              </div>

              <div className="bg-white rounded-xl p-5 md:p-6 shadow-md text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-primary to-secondary rounded-full mx-auto mb-3 md:mb-4">
                  <i className="ri-roadster-line text-white text-2xl md:text-3xl"></i>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">도심 최적화</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600">좁은 골목길도 자유롭게 이동</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      </div>
    </>
  );
}
